import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { t } from '@lingui/macro';
import { useRoute } from '@react-navigation/native';

import SignUpOTP from '@src/api/SignUpOTP';
import SignUpPhone from '@src/api/SignUpPhone';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useInterval from '@src/hooks/useInterval';
import useLanguageState from '@src/hooks/useLanguageState';
import { i18n, i18yup } from '@src/i18n';

import { FormData, ScreenRouteProp } from './types';

const useForm = () => {
  const route = useRoute<ScreenRouteProp>();
  const { setTokens } = useAuthenticationTokens();
  const { setLanguage } = useLanguageState();
  const axiosErrorHandler = useAxiosErrorHandler();

  const [loading, setLoading] = React.useState(false);
  const [ttl, setTtl] = React.useState(Math.floor(route.params.ttl));
  const form = Form.useForm<FormData>({
    validationSchema: i18yup.object<FormData>({
      token: i18yup
        .string()
        .matches(/^[0-9]+$/, i18n._(t`Must contain only digits`))
        .required()
        .label(i18n._(t`Token`)),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ token }: FormData) => {
      try {
        Keyboard.dismiss();
        setLoading(true);
        const response = await SignUpOTP({
          device: DeviceInfo.getDeviceId(),
          deviceId: DeviceInfo.getUniqueId(),
          os: Platform.OS,
          preToken: route.params.preToken,
          token: token,
          type: 'PHONE',
          phone: route.params.phone,
        });
        const accessToken = response.headers['x-auth-token'];
        if (accessToken) {
          setTokens({
            refreshToken: response.data.refreshToken,
            accessToken,
            userID: response.data.userId,
            region: response.data.region,
            setupComplete: response.data.setupComplete,
          });
          if (response.data.region === 'IR') {
            setLanguage('fa');
          }
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.log(e);
        setLoading(false);
      }
    },
    [route.params.phone, route.params.preToken, setLanguage, setTokens]
  );

  const resendCode = React.useCallback(async () => {
    const phone = route.params.phone;
    const region = route.params.region;

    if (!phone || !region) {
      return;
    }

    try {
      const response = await SignUpPhone({ phone, region });
      setTtl(Math.floor(response.data.ttl));
    } catch (error) {
      axiosErrorHandler(error);
    }
  }, [axiosErrorHandler, route.params.phone, route.params.region]);

  useInterval(() => setTtl(ttl - 1), 1000);

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
      ttl,
      resendCode,
    }),
    [form, handleSubmit, loading, resendCode, ttl]
  );
};

export default useForm;
